import React, { useState } from "react";
import { MAP_VIEW, MAP_MARKERS, MAP_EXPANSION, EUROPE_PATH } from "../../data/europeMapData";

// Suceava HQ — origin point for the expansion arrows
const HUB = { x: 708.3, y: 397.1 };

const curve = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const cx = mx - dy * 0.22;
  const cy = my + dx * 0.22;
  return { d: `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`, cx, cy };
};

// Manually built arrowhead triangle (avoids Safari/iOS bugs with SVG marker-end
// rendering on paths animated via CSS transforms).
const arrowHead = (cx, cy, tx, ty, size = 9) => {
  const angle = Math.atan2(ty - cy, tx - cx);
  const spread = 0.45;
  const p1x = tx - size * Math.cos(angle - spread);
  const p1y = ty - size * Math.sin(angle - spread);
  const p2x = tx - size * Math.cos(angle + spread);
  const p2y = ty - size * Math.sin(angle + spread);
  return `M ${tx} ${ty} L ${p1x} ${p1y} L ${p2x} ${p2y} Z`;
};

export const EuropeMap = ({ lang = "ro" }) => {
  const [hover, setHover] = useState(null);

  return (
    <div className="relative w-full overflow-hidden rounded-3xl border border-white/10 bg-[#070c16]">
      <div className="pointer-events-none absolute inset-0 dot-bg opacity-20" />
      <svg
        viewBox={`0 0 ${MAP_VIEW.w} ${MAP_VIEW.h}`}
        className="relative block h-auto w-full"
        role="img"
        aria-label={lang === "ro" ? "Harta Europei cu zonele de activitate" : "Map of Europe with areas of activity"}
      >
        <defs>
          <linearGradient id="land" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#122138" />
            <stop offset="100%" stopColor="#0c1729" />
          </linearGradient>
          <radialGradient id="pinGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ff4d4d" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#ff4d4d" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Landmass */}
        <path d={EUROPE_PATH} fill="url(#land)" stroke="#2f4c78" strokeWidth="0.8" strokeLinejoin="round" />

        {/* Expansion arrows */}
        {MAP_EXPANSION.map((e) => {
          const { d, cx, cy } = curve(HUB, e);
          return (
            <g key={e.label}>
              <path
                d={d}
                fill="none"
                stroke="#4fc3ff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="6 7"
                opacity="0.9"
              />
              <path d={arrowHead(cx, cy, e.x, e.y)} fill="#4fc3ff" opacity="0.9" />
              <circle cx={e.x} cy={e.y} r="4" fill="#4fc3ff" />
              <text
                x={e.x}
                y={e.y - 9}
                textAnchor="middle"
                fill="#9fd8ff"
                fontSize="12.5"
                fontWeight="500"
                opacity="0.85"
              >
                {e.label}
              </text>
            </g>
          );
        })}

        {/* Worked-city pins */}
        {MAP_MARKERS.map((m) => (
          <g
            key={m.city}
            onMouseEnter={() => setHover(m.city)}
            onMouseLeave={() => setHover(null)}
            style={{ cursor: "pointer" }}
          >
            <circle cx={m.x} cy={m.y} r="20" fill="url(#pinGlow)" className="animate-pulse-glow" />
            <circle cx={m.x} cy={m.y} r="6" fill="#ff3b3b" stroke="#fff" strokeWidth="1.5" />
            {hover === m.city && (
              <g>
                <rect
                  x={m.x + 10}
                  y={m.y - 26}
                  width={m.city.length * 9.5 + 18}
                  height="24"
                  rx="6"
                  fill="#0b0f14"
                  stroke="#ff3b3b"
                  strokeWidth="1"
                />
                <text x={m.x + 19} y={m.y - 9} fill="#fff" fontSize="14" fontWeight="600">
                  {m.city}
                </text>
              </g>
            )}
          </g>
        ))}
      </svg>

      {/* Legend */}
      <div className="flex flex-col items-start gap-3 border-t border-white/10 px-6 py-4 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-2">
        <span className="inline-flex items-center gap-2 text-white/70">
          <span className="h-3 w-3 shrink-0 rounded-full bg-[#ff3b3b] shadow-[0_0_8px_2px_rgba(255,59,59,0.7)]" />
          {lang === "ro" ? "Orașe în care am lucrat" : "Cities we've worked in"}
        </span>
        <span className="inline-flex items-center gap-2 text-white/70">
          <span className="h-0.5 w-6 shrink-0 rounded-full bg-glow-2 shadow-[0_0_8px_1px_rgba(79,195,255,0.6)]" />
          {lang === "ro" ? "Direcții de extindere" : "Expansion directions"}
        </span>
      </div>
    </div>
  );
};
